import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../supabaseClient';
import type { User } from '../types';

interface AuthContextType {
  user: User | null;
  authLoading: boolean;
  isPasswordRecovery: boolean;
  handleLogout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);

  useEffect(() => {
    // Detección sincrónica del link de recovery en la URL.
    // El evento PASSWORD_RECOVERY de Supabase se dispara durante la
    // inicialización del cliente (antes de que React monte y suscriba),
    // por eso también lo detectamos directamente desde la URL.
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const searchParams = new URLSearchParams(window.location.search);
    if (hashParams.get('type') === 'recovery' || searchParams.get('type') === 'recovery') {
      setIsPasswordRecovery(true);
    }

    // Suscribirse ANTES de getSession para no perder eventos tempranos
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsPasswordRecovery(true);
      } else if (event === 'SIGNED_IN' || event === 'USER_UPDATED' || event === 'SIGNED_OUT') {
        // SIGNED_IN cubre login normal luego de un token inválido/expirado.
        // PASSWORD_RECOVERY y SIGNED_IN son mutuamente excluyentes en Supabase,
        // así que este reset no interfiere con el flujo de recovery válido.
        setIsPasswordRecovery(false);
      }
      // Solo actualizar user cuando el ID realmente cambia, para evitar que
      // TOKEN_REFRESHED (que Supabase dispara al volver de background) cree
      // un nuevo objeto y provoque un reload completo de datos.
      const newUserId = session?.user?.id ?? null;
      setUser(prev => {
        if ((prev?.id ?? null) === newUserId) return prev;
        return session?.user ?? null;
      });
      setAuthLoading(false);
    });

    // Verificar sesión actual después de suscribirse
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setAuthLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, authLoading, isPasswordRecovery, handleLogout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
